import * as React from 'react';
import { styled } from '@glitz/react';
import { type PropType as ButtonPropType, FeedbackStandaloneButton } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { pushState, translate, useDynamicData } from '@avensia/excite';
import { CartIcon } from 'webapp/components/icons';
import { a11yGrey, black } from 'webapp/shared/sharedTheme';
import { createButtonHoverDecorator } from 'webapp/utils/button-hover';
import { addBundle, addLineItem, addPackage } from 'webapp/pages/checkout/commands';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { ScaleAnimation } from '@avensia/excite-ui';

type PropType = ButtonPropType & {
  product: NordicNest.Api.PartialProductApiModel;
  setValidationMessage: (message: string | undefined) => void;
  additionalProductAttributes?: object;
};
export const AddToCartButton = styled(
  ({ product, setValidationMessage, additionalProductAttributes, ...restProps }: PropType) => {
    const [{ itemCount, addedToCart }] = useDynamicData<
      Excite.Commercetools.CartApiModel,
      { itemCount: number; addedToCart: boolean }
    >({
      selector(data) {
        return {
          itemCount: data?.items?.find(item => item.sku === product.sku)?.quantity ?? 0,
          addedToCart: data?.items?.find(item => item.sku === product.sku) !== undefined || false,
        };
      },
    });
    const [isLoading, setIsLoading] = React.useState(false);

    return (
      <Wrapper>
        <Button
          {...restProps}
          aria-label={translate(t => t.product.addToCartButtonText)}
          onClick={() => addToCart(setValidationMessage, setIsLoading, product, 1, additionalProductAttributes)}
          as={Theme.Transparent}
          disableRippleEffect
          {...NnTestIdProps.ProductCardAddToCart}
        >
          <StyledCartIcon css={addedToCart && addedToCartDecorator} />
        </Button>
        <ItemCount visible={!isLoading && itemCount > 1}>{itemCount}</ItemCount>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div({
  position: 'relative',
});

const Button = styled(
  FeedbackStandaloneButton,
  {
    padding: { xy: 0 },
    fontSize: t => t.sharedFontSize.xxs,
    border: { xy: { width: 0 } },
  },
  createButtonHoverDecorator({
    color: a11yGrey,
  }),
);

const StyledCartIcon = styled(CartIcon, {
  fontSize: t => t.sharedFontSize.s,
  color: black,
  ':hover': {
    color: a11yGrey,
  },
});

const addedToCartDecorator = styled({
  fill: black,
  ':hover': {
    fill: a11yGrey,
  },
});

const ItemCount = styled(ScaleAnimation, {
  color: black,
  backgroundColor: 'white',
  position: 'absolute',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 20,
  width: 20,
  top: -6,
  right: -7,
  borderRadius: '50%',
  fontSize: t => t.sharedFontSize.xxxs,
  pointerEvents: 'none',
});

function setNotLoadingAfterDelay(setIsLoading: (isLoading: boolean) => void) {
  setTimeout(() => {
    setIsLoading(false);
  }, 2000);
}

function addToCart(
  setValidationMessage: (message: string | undefined) => void,
  setIsLoading: (isLoading: boolean) => void,
  product: NordicNest.Api.PartialProductApiModel,
  quantity: number = 1,
  additionalProductAttributes?: object,
): void | Promise<any> {
  return new Promise((resolve, reject) => {
    setIsLoading(true);
    if (!product.isPackage && !product.isBundle) {
      return addLineItem(product.sku, quantity, {
        eventData: {
          sku: product.sku,
          itemIdentifier: product.ticket,
          additionalProductAttributes,
        },
      }).then(response => {
        setNotLoadingAfterDelay(setIsLoading);
        return SolveResponse(response, resolve, reject, setValidationMessage);
      });
    } else if (product.quickBuyPackage) {
      const parts = Object.keys(product.quickBuyPackage.packagePartsWithOneChoice).map(key => ({
        packagePartKey: key,
        sku: product.quickBuyPackage?.packagePartsWithOneChoice[key] || '',
      }));

      return addPackage(product.quickBuyPackage.packageKey, parts, quantity, {
        eventData: { sku: product.sku, itemIdentifier: product.ticket, additionalProductAttributes },
      }).then(response => {
        setNotLoadingAfterDelay(setIsLoading);
        return SolveResponse(response, resolve, reject, setValidationMessage);
      });
    } else if (product.isBundle) {
      return addBundle(product.sku, 1).then(response => {
        setNotLoadingAfterDelay(setIsLoading);
        return SolveResponse(response, resolve, reject, setValidationMessage);
      });
    } else {
      setNotLoadingAfterDelay(setIsLoading);
      reject();
      return pushState(product.url);
    }
  });
}

function SolveResponse(
  response: Excite.Commercetools.CartApiModel | Excite.Commercetools.CartValidationApiModel,
  resolve: (value: any) => void,
  reject: (reason?: any) => void,
  setValidationMessage: (message: string | undefined) => void,
): any {
  let messageUnchanged = true;
  const validation = response as Excite.Commercetools.CartValidationApiModel;
  if (validation?.hasErrors) {
    if (validation.validationErrors.find(e => e.code === 'insufficient-inventory')) {
      setValidationMessage(translate(t => t.productStockStatus.outOfStockMessage));
      messageUnchanged = false;
    } else if (validation.validationErrors.find(e => e.code === 'maximum-line-item')) {
      setValidationMessage(translate(t => t.productStockStatus.maxLineItemMessage));
      messageUnchanged = false;
    }
    reject();
  } else resolve(response);

  if (messageUnchanged) setValidationMessage(undefined);
}
